/**
 * ngxh6 notifications (navbar bell + /user-admin/notifications), per persona.
 * Wording avoids hard numbers so it can never disagree with a KPI page.
 */
import { addDays, currentQuarter, iso, quarterOf } from '../../clock';
import { Persona } from '../../personas';
import { getState } from '../../state';
import { fakeUlid } from '../../world';
import { daysAgoAt, minutesAgo } from './time';

export interface NotificationJson {
    id: string;
    string: string;
    title: string;
    content: string;
    url: string | null;
    unread: boolean;
    created: string;
}

type Seed = [title: string, text: string, url: string | null, when: Date, unread: boolean];

function lastQuarterLabel(): string {
    const q = quarterOf(addDays(currentQuarter().start, -1));
    return `Q${q.q} FY${String(q.fy).slice(2)}`;
}

function seedsFor(p: Persona): Seed[] {
    const lq = lastQuarterLabel();
    const shared: Record<string, Seed> = {
        sync: ['Nightly sync complete', 'Overnight data sync finished — all dashboards are up to date.', null, daysAgoAt(0, 2, 14), false],
        incidents: ['New incidents', 'New client incidents were recorded overnight and are ready for review.', '/clients/incidents', minutesAgo(95), true],
        overdue: ['Incident reviews due', 'Some incident reviews are past their due date.', '/clients/incidents', daysAgoAt(1, 8, 30), true],
        compliance: ['Compliance expiring', 'Staff screening checks are due to expire this month.', '/people/compliance', daysAgoAt(2, 7, 0), true],
        finance: ['Finance data refreshed', 'Month-end figures have been imported from MYOB.', '/finance', daysAgoAt(3, 6, 45), false],
        report: ['Quarterly report ready', `The ${lq} quarterly report is ready to review.`, '/reports/quarterly-ceo', daysAgoAt(9, 9, 0), false],
        training: ['Training overdue', 'Mandatory training has fallen overdue for some staff.', '/people/training', daysAgoAt(4, 7, 0), false],
        referral: ['New referral', 'A new service referral has been received.', '/clients/referrals', minutesAgo(260), true],
        meds: ['Medication follow-up', 'Some medication administration records need follow-up.', '/clients/medications', daysAgoAt(1, 15, 20), false],
        starters: ['New starters', 'New starters have been added from Employment Hero.', '/people/employment-hero', daysAgoAt(1, 6, 5), true],
        support: ['Support request updated', 'The support team replied to one of your requests.', null, daysAgoAt(1, 16, 5), true],
        board: ['Board pack ready', `The ${lq} board dashboard figures are final.`, '/board', daysAgoAt(6, 10, 0), true],
        boardFinance: ['Finance update', 'Year-to-date finance results have been refreshed.', '/finance', daysAgoAt(3, 6, 45), false],
        budget: ['Budget variance', 'Budget versus actuals has been updated for the latest month.', '/finance/budgets', daysAgoAt(5, 8, 0), false],
    };
    switch (p.key) {
        case 'ceo':
            return [shared['support'], shared['overdue'], shared['report'], shared['compliance'], shared['finance'], shared['budget'], shared['sync']];
        case 'coo':
            return [shared['incidents'], shared['support'], shared['overdue'], shared['finance'], shared['training'], shared['sync']];
        case 'care':
            return [shared['referral'], shared['incidents'], shared['overdue'], shared['meds'], shared['sync']];
        case 'hr':
            return [shared['starters'], shared['compliance'], shared['support'], shared['training'], shared['sync']];
        case 'board':
            return [shared['board'], shared['boardFinance']];
        default:
            return [shared['sync']];
    }
}

/** This persona's notifications, newest first (mutable: mark-read flips `unread`). */
export function notificationsFor(p: Persona): NotificationJson[] {
    return getState(`admin:notifications:${p.key}`, () =>
        seedsFor(p)
            .map(([title, text, url, when, unread], i) => ({
                id: fakeUlid(`notification:${p.key}:${i}`),
                string: text,
                title,
                content: text,
                url,
                unread,
                created: iso(when),
            }))
            .sort((a, b) => b.created.localeCompare(a.created)),
    );
}

export function unreadNotificationCount(p: Persona): number {
    return notificationsFor(p).filter(n => n.unread).length;
}

export function markNotificationRead(p: Persona, id: string): void {
    const n = notificationsFor(p).find(x => x.id === id);
    if (n) {
        n.unread = false;
    }
}

export function markAllNotificationsRead(p: Persona): void {
    notificationsFor(p).forEach(n => (n.unread = false));
}
